import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { 
  Shield, Search, Download, RefreshCw, 
  CheckCircle2, XCircle, AlertTriangle, Loader2,
  ChevronLeft, ChevronRight, Lock, Unlock, Globe, Ban, Activity
} from 'lucide-react';
import { useSecurityAuditLogs, useSecurityAuditStats } from '../../hooks/useAdvancedSecurity';
import { useAuthStore } from '../../stores/auth.store';
import { api } from '../../lib/api';
import type { SecurityEventType, SecurityAuditQuery } from '../../types/advanced-security';

const eventTypeLabels: Record<SecurityEventType, { label: string; color: string }> = {
  FACIAL_VERIFICATION_SUCCESS: { label: 'Facial OK', color: 'bg-green-100 text-green-700' },
  FACIAL_VERIFICATION_FAILED: { label: 'Facial Falhou', color: 'bg-red-100 text-red-700' },
  SIGNATURE_VERIFICATION_SUCCESS: { label: 'Assinatura OK', color: 'bg-green-100 text-green-700' },
  SIGNATURE_VERIFICATION_FAILED: { label: 'Assinatura Falhou', color: 'bg-red-100 text-red-700' },
  COMBINED_VERIFICATION_SUCCESS: { label: 'Combinada OK', color: 'bg-green-100 text-green-700' },
  COMBINED_VERIFICATION_FAILED: { label: 'Combinada Falhou', color: 'bg-red-100 text-red-700' },
  LIVENESS_SESSION_STARTED: { label: 'Liveness Iniciado', color: 'bg-blue-100 text-blue-700' },
  LIVENESS_CHALLENGE_PASSED: { label: 'Desafio OK', color: 'bg-green-100 text-green-700' },
  LIVENESS_CHALLENGE_FAILED: { label: 'Desafio Falhou', color: 'bg-red-100 text-red-700' },
  LIVENESS_SESSION_COMPLETED: { label: 'Liveness Completo', color: 'bg-green-100 text-green-700' },
  LIVENESS_SESSION_FAILED: { label: 'Liveness Falhou', color: 'bg-red-100 text-red-700' },
  GEOLOCATION_VALIDATED: { label: 'Localização OK', color: 'bg-green-100 text-green-700' },
  GEOLOCATION_BLOCKED: { label: 'Localização Bloqueada', color: 'bg-red-100 text-red-700' },
  UNUSUAL_LOCATION_DETECTED: { label: 'Local Incomum', color: 'bg-amber-100 text-amber-700' },
  IMPOSSIBLE_TRAVEL_DETECTED: { label: 'Viagem Impossível', color: 'bg-red-100 text-red-700' },
  BLOCKCHAIN_SIGNATURE_REGISTERED: { label: 'Blockchain Registrado', color: 'bg-purple-100 text-purple-700' },
  BLOCKCHAIN_SIGNATURE_VERIFIED: { label: 'Blockchain Verificado', color: 'bg-purple-100 text-purple-700' },
  VOICE_REGISTRATION_SUCCESS: { label: 'Voz Registrada', color: 'bg-green-100 text-green-700' },
  VOICE_REGISTRATION_FAILED: { label: 'Registro Voz Falhou', color: 'bg-red-100 text-red-700' },
  VOICE_VERIFICATION_SUCCESS: { label: 'Voz OK', color: 'bg-green-100 text-green-700' },
  VOICE_VERIFICATION_FAILED: { label: 'Voz Falhou', color: 'bg-red-100 text-red-700' },
  VOICE_SPOOFING_DETECTED: { label: 'Spoofing Detectado', color: 'bg-red-100 text-red-700' },
  CONFIG_UPDATED: { label: 'Config Atualizada', color: 'bg-gray-100 text-gray-700' },
};

export function SecurityAuditLogs() {
  const { user } = useAuthStore();
  const tenantId = user?.tenantId;
  const [activeTab, setActiveTab] = useState<'audit' | 'bruteforce'>('audit');
  
  const [query, setQuery] = useState<SecurityAuditQuery>({
    tenantId,
    page: 1,
    limit: 20,
  });
  
  const [searchUserId, setSearchUserId] = useState('');
  
  const { data: logsData, isLoading, refetch } = useSecurityAuditLogs(query);
  const { data: stats } = useSecurityAuditStats(tenantId);

  const handleSearch = () => {
    setQuery(prev => ({
      ...prev,
      userId: searchUserId || undefined,
      page: 1,
    }));
  };

  const handleEventTypeFilter = (eventType: string) => {
    setQuery(prev => ({
      ...prev,
      eventType: eventType === 'all' ? undefined : eventType as SecurityEventType,
      page: 1,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setQuery(prev => ({ ...prev, page: newPage }));
  };

  const totalPages = logsData ? Math.ceil(logsData.total / (query.limit || 20)) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Segurança e Auditoria
          </h1>
          <p className="text-gray-500 mt-1">
            Monitore eventos de segurança e proteção contra ataques
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'audit'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Activity className="w-4 h-4 inline mr-2" />
          Logs de Auditoria
        </button>
        <button
          onClick={() => setActiveTab('bruteforce')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'bruteforce'
              ? 'border-red-500 text-red-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Lock className="w-4 h-4 inline mr-2" />
          Proteção Brute Force
        </button>
      </div>

      {activeTab === 'bruteforce' && <BruteForcePanel />}

      {activeTab === 'audit' && (
      <>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.totalEvents}</div>
              <p className="text-sm text-gray-500">Total de Eventos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {(stats.successRate * 100).toFixed(1)}%
              </div>
              <p className="text-sm text-gray-500">Taxa de Sucesso</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-600">{stats.recentFailures}</div>
              <p className="text-sm text-gray-500">Falhas Recentes (24h)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {Object.keys(stats.eventsByType).length}
              </div>
              <p className="text-sm text-gray-500">Tipos de Evento</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Buscar por Usuário</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="ID do usuário..."
                  value={searchUserId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchUserId(e.target.value)}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="w-[200px]">
              <Label>Tipo de Evento</Label>
              <Select
                value={query.eventType || 'all'}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleEventTypeFilter(e.target.value)}
              >
                <option value="all">Todos</option>
                {Object.entries(eventTypeLabels).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Select>
            </div>
            
            <div className="w-[150px]">
              <Label>Data Início</Label>
              <Input
                type="date"
                value={query.startDate || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(prev => ({ ...prev, startDate: e.target.value, page: 1 }))}
              />
            </div>
            
            <div className="w-[150px]">
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={query.endDate || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(prev => ({ ...prev, endDate: e.target.value, page: 1 }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Eventos de Segurança</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : !logsData?.logs.length ? (
            <div className="text-center py-8 text-gray-500">
              Nenhum evento encontrado
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Data/Hora</th>
                      <th className="text-left py-3 px-4 font-medium">Tipo</th>
                      <th className="text-left py-3 px-4 font-medium">Usuário</th>
                      <th className="text-left py-3 px-4 font-medium">IP</th>
                      <th className="text-left py-3 px-4 font-medium">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsData.logs.map((log) => {
                      const eventInfo = eventTypeLabels[log.eventType];
                      const isSuccess = log.eventType.includes('SUCCESS') || 
                                       log.eventType.includes('COMPLETED') ||
                                       log.eventType.includes('VALIDATED') ||
                                       log.eventType.includes('REGISTERED') ||
                                       log.eventType.includes('VERIFIED') ||
                                       log.eventType.includes('PASSED');
                      const isFailure = log.eventType.includes('FAILED') || 
                                       log.eventType.includes('BLOCKED') ||
                                       log.eventType.includes('DETECTED');
                      
                      return (
                        <tr key={log.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm">
                            {new Date(log.createdAt).toLocaleString('pt-BR')}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={eventInfo?.color || 'bg-gray-100'}>
                              {isSuccess && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {isFailure && <XCircle className="h-3 w-3 mr-1" />}
                              {!isSuccess && !isFailure && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {eventInfo?.label || log.eventType}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm font-mono">
                            {log.userId ? log.userId.slice(0, 8) + '...' : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm font-mono">
                            {log.ipAddress || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500 max-w-xs truncate">
                            {log.details ? JSON.stringify(log.details).slice(0, 50) + '...' : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Mostrando {((query.page || 1) - 1) * (query.limit || 20) + 1} a{' '}
                  {Math.min((query.page || 1) * (query.limit || 20), logsData.total)} de{' '}
                  {logsData.total} eventos
                </p>
                
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handlePageChange((query.page || 1) - 1)}
                    disabled={(query.page || 1) <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="flex items-center px-3 text-sm">
                    Página {query.page || 1} de {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handlePageChange((query.page || 1) + 1)}
                    disabled={(query.page || 1) >= totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}

// ============================================
// Brute Force Protection Panel
// ============================================

function BruteForcePanel() {
  const queryClient = useQueryClient();

  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['brute-force', 'stats'],
    queryFn: async () => { const r = await api.get('/admin/security/brute-force/stats'); return r.data; },
    refetchInterval: 10000,
  });

  const { data: lockedAccounts, refetch: refetchLocked } = useQuery({
    queryKey: ['brute-force', 'locked'],
    queryFn: async () => { const r = await api.get('/admin/security/brute-force/locked-accounts'); return r.data; },
    refetchInterval: 10000,
  });

  const { data: blockedIps, refetch: refetchIps } = useQuery({
    queryKey: ['brute-force', 'blocked-ips'],
    queryFn: async () => { const r = await api.get('/admin/security/brute-force/blocked-ips'); return r.data; },
    refetchInterval: 10000,
  });

  const { data: recentFailures } = useQuery({
    queryKey: ['brute-force', 'failures'],
    queryFn: async () => { const r = await api.get('/admin/security/brute-force/recent-failures?limit=30'); return r.data; },
  });

  const unlockMutation = useMutation({
    mutationFn: async (data: { email: string; tenantId?: string }) => {
      const r = await api.post('/admin/security/brute-force/unlock-account', data);
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brute-force'] });
    },
  });

  const unblockIpMutation = useMutation({
    mutationFn: async (data: { ip: string }) => {
      const r = await api.post('/admin/security/brute-force/unblock-ip', data);
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brute-force'] });
    },
  });

  const fmtDate = (d: string) => {
    try { return new Date(d).toLocaleString('pt-BR'); } catch { return d; }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6">
          <div className="text-2xl font-bold text-red-600">{stats?.lockedAccounts || 0}</div>
          <p className="text-sm text-gray-500">Contas Bloqueadas</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="text-2xl font-bold text-orange-600">{stats?.blockedIps || 0}</div>
          <p className="text-sm text-gray-500">IPs Bloqueados</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="text-2xl font-bold text-blue-600">{stats?.totalTrackedAccounts || 0}</div>
          <p className="text-sm text-gray-500">Contas Monitoradas</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="text-2xl font-bold text-purple-600">{stats?.totalTrackedIps || 0}</div>
          <p className="text-sm text-gray-500">IPs Monitorados</p>
        </CardContent></Card>
      </div>

      {/* Locked Accounts */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-red-500" /> Contas Bloqueadas</CardTitle></CardHeader>
        <CardContent>
          {!lockedAccounts?.length ? (
            <p className="text-gray-500 text-center py-4">Nenhuma conta bloqueada no momento</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="text-left py-2 px-3">Email</th><th className="text-left py-2 px-3">Tentativas</th><th className="text-left py-2 px-3">Bloqueado até</th><th className="text-right py-2 px-3">Ação</th></tr></thead>
                <tbody>
                  {lockedAccounts.map((a: any, i: number) => (
                    <tr key={i} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-2 px-3 font-mono text-xs">{a.email}</td>
                      <td className="py-2 px-3"><Badge className="bg-red-100 text-red-700">{a.count}x</Badge></td>
                      <td className="py-2 px-3 text-xs">{fmtDate(a.lockedUntil)}</td>
                      <td className="py-2 px-3 text-right">
                        <Button size="sm" variant="secondary" onClick={() => unlockMutation.mutate({ email: a.email })} disabled={unlockMutation.isPending}>
                          <Unlock className="w-3 h-3 mr-1" /> Desbloquear
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blocked IPs */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5 text-orange-500" /> IPs Bloqueados</CardTitle></CardHeader>
        <CardContent>
          {!blockedIps?.length ? (
            <p className="text-gray-500 text-center py-4">Nenhum IP bloqueado no momento</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="text-left py-2 px-3">IP</th><th className="text-left py-2 px-3">Tentativas</th><th className="text-left py-2 px-3">Bloqueado até</th><th className="text-right py-2 px-3">Ação</th></tr></thead>
                <tbody>
                  {blockedIps.map((ip: any, i: number) => (
                    <tr key={i} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-2 px-3 font-mono">{ip.ip}</td>
                      <td className="py-2 px-3"><Badge className="bg-orange-100 text-orange-700">{ip.count}x</Badge></td>
                      <td className="py-2 px-3 text-xs">{fmtDate(ip.blockedUntil)}</td>
                      <td className="py-2 px-3 text-right">
                        <Button size="sm" variant="secondary" onClick={() => unblockIpMutation.mutate({ ip: ip.ip })} disabled={unblockIpMutation.isPending}>
                          <Ban className="w-3 h-3 mr-1" /> Desbloquear
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Failed Attempts */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-yellow-500" /> Tentativas de Login Falhadas (Recentes)</CardTitle></CardHeader>
        <CardContent>
          {!recentFailures?.length ? (
            <p className="text-gray-500 text-center py-4">Nenhuma tentativa falhada registrada</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="text-left py-2 px-3">Data/Hora</th><th className="text-left py-2 px-3">Email</th><th className="text-left py-2 px-3">IP</th><th className="text-left py-2 px-3">Tentativa #</th><th className="text-left py-2 px-3">Status</th></tr></thead>
                <tbody>
                  {recentFailures.map((f: any, i: number) => {
                    const data = f.newData || {};
                    return (
                      <tr key={f.id || i} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="py-2 px-3 text-xs">{fmtDate(f.createdAt)}</td>
                        <td className="py-2 px-3 font-mono text-xs">{data.email || f.resourceId || '-'}</td>
                        <td className="py-2 px-3 font-mono text-xs">{data.ip || '-'}</td>
                        <td className="py-2 px-3">{data.attemptCount || '-'}</td>
                        <td className="py-2 px-3">
                          {data.accountLocked ? <Badge className="bg-red-100 text-red-700">Conta Bloqueada</Badge> :
                           data.ipBlocked ? <Badge className="bg-orange-100 text-orange-700">IP Bloqueado</Badge> :
                           <Badge className="bg-yellow-100 text-yellow-700">Falha</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SecurityAuditLogs;
